import { Suspense } from 'react';

import { Navigate, Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { FeatureFlagGuard } from '@/components/routes/FeatureFlagGuard';
import { RoleGuard } from '@/components/routes/RoleGuard';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// /organization/* routes (plus the /tokens back-compat redirects, the
// /partner/stripe/* Stripe Connect return legs of the payout-settings flow,
// and the /organization/:tab workspace shell). Mounted inside
// ProtectedRoute → TOSGuard → AppLayout in App.tsx; the guards live on that
// shared scaffold, not here.
// ============================================================================

// Organization Pages
// GW-8139: OrgMembersRolePage/OrganizationSummaryPage/OrganizationTransactionsPage/
// OrganizationRFIListPage/OrganizationConsultantListPage/OrganizationTOSPage are
// now imported directly by OrganizationWorkspacePage (the shared tab shell)
// rather than lazily here — only their own detail/action routes remain
// independent.
const InvitationsPage = lazyWithRetry(() =>
  import('@/pages/organization/InvitationsPage').then((m) => ({ default: m.InvitationsPage }))
);
const PayoutSettingsPage = lazyWithRetry(() =>
  import('@/pages/organization/PayoutSettingsPage').then((m) => ({
    default: m.PayoutSettingsPage,
  }))
);
const ProviderAgreementPage = lazyWithRetry(() =>
  import('@/pages/organization/ProviderAgreementPage').then((m) => ({
    default: m.ProviderAgreementPage,
  }))
);
// GW-3060: RFI/Services org pages
const OrganizationServiceListPage = lazyWithRetry(() =>
  import('@/pages/organization/OrganizationServiceListPage').then((m) => ({
    default: m.OrganizationServiceListPage,
  }))
);
const ServiceWizardPage = lazyWithRetry(() =>
  import('@/pages/organization/ServiceWizardPage').then((m) => ({
    default: m.ServiceWizardPage,
  }))
);
const OrganizationRFIDetailPage = lazyWithRetry(() =>
  import('@/pages/organization/OrganizationRFIDetailPage').then((m) => ({
    default: m.OrganizationRFIDetailPage,
  }))
);
// GW-8139: Shared TabbedDetailPage shell for /organization (target IA §5).
const OrganizationWorkspacePage = lazyWithRetry(() =>
  import('@/pages/organization/OrganizationWorkspacePage').then((m) => ({
    default: m.OrganizationWorkspacePage,
  }))
);
// GW-8139: TokensPage/UsageAnalyticsPage are now imported directly by
// OrganizationWorkspacePage (the shared tab shell) rather than lazily here.
const UserUsageAnalyticsPage = lazyWithRetry(() =>
  import('@/pages/protected/UserUsageAnalyticsPage').then((m) => ({
    default: m.UserUsageAnalyticsPage,
  }))
);
// Token Pages
const GiftCardsPage = lazyWithRetry(() =>
  import('@/pages/tokens/GiftCardsPage').then((m) => ({ default: m.GiftCardsPage }))
);
// Organization Pages - Referral Codes (GW-1842)
const ReferralCodesPage = lazyWithRetry(() =>
  import('@/pages/organization/ReferralCodesPage').then((m) => ({
    default: m.ReferralCodesPage,
  }))
);
// Partner Pages (GW-478: Stripe Connect return URL pages)
const StripeCompletePage = lazyWithRetry(() =>
  import('@/pages/partner/stripe/StripeCompletePage').then((m) => ({
    default: m.StripeCompletePage,
  }))
);
const StripeRefreshPage = lazyWithRetry(() =>
  import('@/pages/partner/stripe/StripeRefreshPage').then((m) => ({
    default: m.StripeRefreshPage,
  }))
);
// NOTE: also declared in App.tsx (both catch-alls) — identical import path, so
// Vite emits a single chunk; per-file wrappers keep them decoupled.
// GW-1993/GW-10439: consolidated 404 page, rendered inside the shared authed
// AppLayout shell that wraps these routes in App.tsx.
const NotFoundPage = lazyWithRetry(() =>
  import('@/pages/public/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

export const organizationRoutes = (
  <>
    <Route
      path="/organization/provider-agreement"
      element={
        <RoleGuard requiredRoles={['org_admin', 'app_admin', 'dataset_admin', 'service_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <ProviderAgreementPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/organization/members/invitations"
      element={
        <RoleGuard requiredRoles="org_admin">
          <Suspense fallback={<PageSkeleton />}>
            <InvitationsPage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/organization/usage-analytics/user/:userId"
      element={
        <RoleGuard requiredRoles="org_admin">
          <Suspense fallback={<PageSkeleton />}>
            <UserUsageAnalyticsPage />
          </Suspense>
        </RoleGuard>
      }
    />
    {/* GW-8055: /organization/general renamed to /organization/summary per target IA */}
    <Route path="/organization/general" element={<Navigate to="/organization/summary" replace />} />
    <Route
      path="/organization/payout-settings"
      element={
        <RoleGuard requiredRoles="org_admin">
          <Suspense fallback={<PageSkeleton />}>
            <PayoutSettingsPage />
          </Suspense>
        </RoleGuard>
      }
    />
    {/* GW-3060 / GW-6343: RFI/Services org pages — broader role guard allows org_admin, service_admin, consultant, app_admin, org_member.
        GW-8665: gated behind services_marketplace. */}
    <Route
      path="/organization/services"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <RoleGuard
            requiredRoles={['org_admin', 'service_admin', 'consultant', 'app_admin', 'org_member']}
          >
            <Suspense fallback={<PageSkeleton />}>
              <OrganizationServiceListPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/organization/services/create"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <RoleGuard requiredRoles={['org_admin', 'service_admin', 'consultant']}>
            <Suspense fallback={<PageSkeleton />}>
              <ServiceWizardPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/organization/services/edit/:id"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <RoleGuard requiredRoles={['org_admin', 'service_admin', 'consultant']}>
            <Suspense fallback={<PageSkeleton />}>
              <ServiceWizardPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    <Route
      path="/organization/rfis/:rfiId"
      element={
        <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
          <RoleGuard requiredRoles={['org_admin', 'service_admin', 'app_admin', 'org_member']}>
            <Suspense fallback={<PageSkeleton />}>
              <OrganizationRFIDetailPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* Backward compatibility redirect */}
    <Route path="/tokens" element={<Navigate to="/organization/tokens" replace />} />

    {/* GW-8060: /organization/consultants renamed to /organization/service-roster */}
    <Route
      path="/organization/consultants"
      element={<Navigate to="/organization/service-roster" replace />}
    />

    {/* GW-1578: Gift Cards routes (feature flagged) - moved to /organization/gift-cards */}
    <Route
      path="/organization/gift-cards"
      element={
        <FeatureFlagGuard flagName="gift_cards_enabled" fallbackRoute="/organization/tokens">
          <RoleGuard requiredRoles={['org_admin', 'app_admin']}>
            <Suspense fallback={<PageSkeleton />}>
              <GiftCardsPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />
    {/* GW-1842: Referral Codes management page (feature flagged).
        GW-8294: referral codes are retired per the target IA
        (docs/target-ia-outline.md §12) but the implementation is
        kept dormant, not deleted, so it can be revived without a
        rebuild — see src/HIDDEN_FEATURES.md. The route previously
        required only `org_admin`, meaning any org_admin in any
        org could reach a fully live referral-code management UI
        (audit findings 05-ISSUE-002/08-ISSUE-005). Tightened to
        `global_admin` — a platform-staff-only role no customer
        org has — via the existing role/permission system
        (RoleGuard), not a new LaunchDarkly-style flag, so the
        route defaults off for every real org today and reverts
        to `org_admin` in one line to re-enable. Non-global_admin
        visitors get the same 404 as any other unmapped route
        (NotFoundPage), matching the "no back-compat
        redirects" standing decision (PRP-GW8255 Decision Log). */}
    <Route
      path="/organization/referrals"
      element={
        <FeatureFlagGuard flagName="referral_codes_enabled" fallbackRoute="/organization/summary">
          <RoleGuard
            requiredRoles="global_admin"
            fallback={
              <Suspense fallback={<PageSkeleton />}>
                <NotFoundPage />
              </Suspense>
            }
          >
            <Suspense fallback={<PageSkeleton />}>
              <ReferralCodesPage />
            </Suspense>
          </RoleGuard>
        </FeatureFlagGuard>
      }
    />

    {/* GW-1578: Backward compatibility redirects for old gift cards routes */}
    <Route
      path="/organization/tokens/gift-cards"
      element={<Navigate to="/organization/gift-cards" replace />}
    />
    <Route path="/tokens/gift-cards" element={<Navigate to="/organization/gift-cards" replace />} />

    {/* Partner routes - Stripe Connect return URLs (GW-478) */}
    <Route
      path="/partner/stripe/complete"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <StripeCompletePage />
          </Suspense>
        </RoleGuard>
      }
    />
    <Route
      path="/partner/stripe/refresh"
      element={
        <RoleGuard requiredRoles={['app_admin', 'org_admin']}>
          <Suspense fallback={<PageSkeleton />}>
            <StripeRefreshPage />
          </Suspense>
        </RoleGuard>
      }
    />

    <Route path="/organization" element={<Navigate to="/organization/summary" replace />} />
    <Route
      path="/organization/:tab"
      element={
        <RoleGuard requiredRoles={['org_admin', 'service_admin', 'app_admin', 'org_member']}>
          <Suspense fallback={<PageSkeleton />}>
            <OrganizationWorkspacePage />
          </Suspense>
        </RoleGuard>
      }
    />
  </>
);
